import {
  linkObjectToRelatedMatter,
  type RelatedMatterObjectType,
  retrieveObjectMatterMemberships,
  unlinkObjectFromMatter,
} from "@/lib/data/requests/matters/matter-related-object";
import { copyPartyInsuranceToRelatedMatters } from "@/lib/data/requests/matters/matter-insurance";
import { copyPartyVehicleToRelatedMatters } from "@/lib/data/requests/matters/matter-vehicle";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError } from "@/lib/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import toast from "react-hot-toast";

export const retrieveObjectMatterMembershipsQueryOptions = ({
  objectType,
  objectId,
  currentMatterId,
  enabled = true,
}: Parameters<typeof retrieveObjectMatterMemberships>[0] & {
  enabled?: boolean;
}) => ({
  queryKey: [
    queryKeys.matters,
    "shared-item-memberships",
    objectType,
    objectId,
    currentMatterId,
  ],
  queryFn: () =>
    retrieveObjectMatterMemberships({
      objectType,
      objectId,
      currentMatterId,
    }),
  enabled: enabled && !!objectId && !!currentMatterId,
  retry: false,
});

export const useUnlinkObjectFromMatter = ({
  objectLabel,
  showToast = true,
}: {
  objectLabel: string;
  showToast?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unlinkObjectFromMatter,
    onSuccess: async (_data, variables) => {
      if (showToast) {
        toast.success(`${objectLabel} unlinked from matter successfully`);
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, variables.matterId],
        }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.matters] }),
      ]);
    },
    onError: (error) => {
      toast.error(
        isAxiosError<APIError>(error)
          ? error.response?.data.meta.userMsg ||
              `Unable to unlink ${objectLabel.toLowerCase()} from matter.`
          : `Unable to unlink ${objectLabel.toLowerCase()} from matter.`,
      );
    },
  });
};

export const useUnlinkObjectFromMatters = ({
  objectLabel,
}: {
  objectLabel: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      currentMatterId,
      matterIds,
      objectId,
      objectType,
    }: {
      currentMatterId: string;
      matterIds: string[];
      objectId: string;
      objectType: Exclude<RelatedMatterObjectType, "incident">;
    }) => {
      const uniqueMatterIds = [...new Set(matterIds)];
      const orderedMatterIds = [
        ...uniqueMatterIds.filter((matterId) => matterId !== currentMatterId),
        ...uniqueMatterIds.filter((matterId) => matterId === currentMatterId),
      ];
      const unlinkedMatterIds: string[] = [];
      const failures: string[] = [];

      for (const matterId of orderedMatterIds) {
        try {
          await unlinkObjectFromMatter({ objectType, objectId, matterId });
          unlinkedMatterIds.push(matterId);
        } catch (error) {
          failures.push(
            isAxiosError<APIError>(error)
              ? error.response?.data.meta.userMsg ||
                  `Unable to delete ${objectLabel.toLowerCase()} from a selected matter.`
              : `Unable to delete ${objectLabel.toLowerCase()} from a selected matter.`,
          );
        }
      }

      return { failures, unlinkedMatterIds };
    },
    onSuccess: async ({ failures, unlinkedMatterIds }, variables) => {
      if (failures.length > 0) {
        toast.error(
          `${unlinkedMatterIds.length ? `Deleted from ${unlinkedMatterIds.length} of ${variables.matterIds.length} matters. ` : ""}${failures[0]}`,
        );
      } else {
        toast.success(
          `${objectLabel} deleted from ${unlinkedMatterIds.length} ${unlinkedMatterIds.length === 1 ? "matter" : "matters"} successfully`,
        );
      }

      await Promise.all([
        ...[...new Set(variables.matterIds)].map((matterId) =>
          queryClient.invalidateQueries({
            queryKey: [queryKeys.matters, matterId],
          }),
        ),
        queryClient.invalidateQueries({ queryKey: [queryKeys.matters] }),
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.matters,
            "shared-item-memberships",
            variables.objectType,
            variables.objectId,
          ],
        }),
      ]);
    },
  });
};

export const useLinkObjectToRelatedMatters = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      objectType,
      objectId,
      matterIds,
    }: {
      objectType: RelatedMatterObjectType;
      objectId: string;
      sourceMatterId: string;
      matterIds: string[];
    }) => {
      const addedMatterIds: string[] = [];
      const failures: string[] = [];
      // Each request locks the shared object; serialize links to avoid competing writes.
      for (const matterId of new Set(matterIds)) {
        try {
          await linkObjectToRelatedMatter({
            objectType,
            objectId,
            matterId,
          });
          addedMatterIds.push(matterId);
        } catch (error) {
          failures.push(
            isAxiosError<APIError>(error)
              ? error.response?.data.meta.userMsg ||
                  "Unable to add to related matter."
              : "Unable to add to related matter.",
          );
        }
      }
      return { addedMatterIds, failures };
    },
    onSuccess: async ({ addedMatterIds, failures }, variables) => {
      if (failures.length) {
        toast.error(
          `${addedMatterIds.length ? `Added to ${addedMatterIds.length} of ${addedMatterIds.length + failures.length} related matters. ` : ""}${failures[0]}`,
        );
      } else {
        toast.success(
          `Added to ${addedMatterIds.length} related ${addedMatterIds.length === 1 ? "matter" : "matters"}.`,
        );
      }
      await Promise.all(
        [...new Set([variables.sourceMatterId, ...variables.matterIds])].map(
          (matterId) =>
            queryClient.invalidateQueries({
              queryKey: [queryKeys.matters, matterId],
            }),
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: [
          queryKeys.matters,
          "shared-item-memberships",
          variables.objectType,
          variables.objectId,
        ],
      });
    },
  });
};

export const useCopyPartyInsuranceToRelatedMatters = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: copyPartyInsuranceToRelatedMatters,
    onSuccess: async (_data, variables) => {
      if (showToast) {
        toast.success(
          `Added to ${variables.placements.length} related ${variables.placements.length === 1 ? "matter" : "matters"}.`,
        );
      }
      await queryClient.invalidateQueries({ queryKey: [queryKeys.matters] });
    },
    onError: (error) => {
      if (showToast) {
        toast.error(
          isAxiosError<APIError>(error)
            ? error.response?.data.meta.userMsg ||
                "Unable to add insurance to related matters."
            : "Unable to add insurance to related matters.",
        );
      }
    },
  });
};

export const useCopyPartyVehicleToRelatedMatters = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: copyPartyVehicleToRelatedMatters,
    onSuccess: async (_data, variables) => {
      if (showToast) {
        toast.success(
          `Added to ${variables.placements.length} related ${variables.placements.length === 1 ? "matter" : "matters"}.`,
        );
      }
      await queryClient.invalidateQueries({ queryKey: [queryKeys.matters] });
    },
    onError: (error) => {
      if (showToast) {
        toast.error(
          isAxiosError<APIError>(error)
            ? error.response?.data.meta.userMsg ||
                "Unable to add vehicle to related matters."
            : "Unable to add vehicle to related matters.",
        );
      }
    },
  });
};
